import { useCallback } from "react";

import {
  type Property,
  type PropertyFragment,
  useCreatePropertyMutation,
  useGetUserProfileLazyQuery,
  useUpdatePropertyMutation,
} from "@stepapp/core";

import { ApartmentType, type InputMaybe } from "@stepapp/core/apiTypes";
import { setUserData } from "../store/user";
import { useAppDispatch } from "./redux";

type PropertyFormValues = Partial<
  Omit<PropertyFragment, "name" | "id" | "isCustomerDefault" | "localizationId" | "type">
> & {
  name?: InputMaybe<string>;
  isDefault?: boolean;
  homeType?: ApartmentType;
  localizationId?: string;
  propertyId?: string;
};

export const useCreateOrUpdateAddress = ({
  property,
  localizationId = "",
  isUserLogged,
}: {
  property?: Property;
  localizationId?: string;
  isUserLogged?: boolean;
}) => {
  const appDispatch = useAppDispatch();

  const [createProperty, { loading: createPropertyLoading }] = useCreatePropertyMutation();
  const [updateProperty, { loading: updatePropertyLoading }] = useUpdatePropertyMutation();
  const [getUserProfile, { loading: getUserProfileLoading }] = useGetUserProfileLazyQuery({
    variables: {
      isCustomerProfile: true,
    },
    fetchPolicy: "network-only",
  });

  const createOrUpdateAddress = useCallback(
    async ({
      homeType,
      isDefault = false,
      city = "",
      postalOrZipCode = "",
      streetAddress1 = "",
      streetAddress2 = "",
      floorArea = 0,
      ...values
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: refactor
    }: PropertyFormValues) => {
      const initialPropertyId = property?.propertyId;

      const parsedProperty = {
        ...property,
        setDefault: isDefault,
        type: homeType,
      };

      const preparedParams = {
        ...values,
        floorArea,
        city,
        postalOrZipCode,
        streetAddress1,
        streetAddress2: streetAddress2 || "",
        setDefault: isDefault,
        type: homeType || ApartmentType.Apartment,
      };

      if (!isUserLogged) return preparedParams;

      if (initialPropertyId) {
        const isSame = Object.entries({ ...preparedParams, propertyId: initialPropertyId }).every(
          ([key, value]) => parsedProperty[key as keyof typeof parsedProperty] === value,
        );

        if (isSame) return preparedParams;

        // OMIT localizationId from updateProperty
        const { localizationId, ...rest } = preparedParams;

        const { data, errors } = await updateProperty({
          variables: { data: { ...rest, propertyId: initialPropertyId } },
        });

        if (errors) alert(errors?.[0]?.message);

        const { data: userData } = await getUserProfile();

        userData && appDispatch(setUserData(userData?.getMyProfile));
        return data?.updateProperty ? preparedParams : false;
      }

      const { propertyId, ...rest } = preparedParams;
      const { data } = await createProperty({ variables: { data: { ...rest, localizationId } } });

      let newPropertyId = "";

      // TODO refactor this once propertyId will be returned from createProperty
      if (data?.createProperty) {
        const { data: userData } = await getUserProfile();

        userData && appDispatch(setUserData(userData?.getMyProfile));

        const properties = userData?.getMyProfile?.profileInformation?.customerMetadata?.properties;
        newPropertyId =
          properties?.find(
            (property) =>
              property.floorArea === rest.floorArea &&
              property.city === rest.city &&
              property.postalOrZipCode === rest.postalOrZipCode &&
              property.streetAddress1 === rest.streetAddress1 &&
              property.streetAddress2 === rest.streetAddress2,
          )?.id || "";
      }

      return data?.createProperty ? { ...rest, propertyId: newPropertyId } : false;
    },
    [
      property,
      isUserLogged,
      updateProperty,
      createProperty,
      localizationId,
      getUserProfile,
      appDispatch,
    ],
  );

  return [
    createOrUpdateAddress,
    { loading: createPropertyLoading || updatePropertyLoading || getUserProfileLoading },
  ] as const;
};
